import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import i18n from 'i18n';
import { RootState } from 'data/root.reducers';
import { ThunkDispatch } from 'redux-thunk';
import { showSuccessMessage, showError } from 'shared/toaster';
import { CollectionFunctionFormData } from 'data/collectionFunction/collectionFunction.types';
import { XTP_XDP_CONNECTION, NETWORK, SYSTEM_CONFIGURATION_ROUTE } from '../../constants';
import { extractErrorMessage } from '../../utils';
import * as api from './xtp-xdp-connection.api';

import {
  UPDATE_XTP_XDP_CONNECTION_ERRORS,
  GO_TO_XTP_XDP_CONNECTION_CREATE,
  GO_TO_XTP_XDP_CONNECTION_EDIT,
  GO_TO_XTP_XDP_CONNECTION_VIEW,
  GO_TO_XTP_XDP_CONNECTION_LIST,
  GET_XTP_XDP_CONNECTION_LIST,
  DELETE_XTP_XDP_CONNECTION,
  CREATE_XTP_XDP_CONNECTION,
  SELECT_XTP_XDP_CONNECTION,
  UPDATE_XTP_XDP_CONNECTION,
  GET_XTP_XDP_CONNECTION,
  GET_XTP_XDP_CONNECTION_PROTOCOL,
  UPDATE_XTP_XDP_CONNECTION_PROTOCOL,
  XtpXdpConnection,
  GetXtpXdpConnectionAction,
  XtpXdpConnectionProtocol,
  CreateXtpXdpConnectionThunkAction,
  UpdateXtpXdpConnectionThunkAction,
  DeleteXtpXdpConnectionThunkAction,
  GetXtpXdpConnectionProtocolsAction,
  GetXtpXdpConnectionActionThunkAction,
  GetXtpXdpConnectionByIdActionThunkAction,
  SelectXtpXdpConnection,
  CREATE_XTP_XDP_CONNECTION_PROTOCOL,
} from './xtp-xdp-connection.types';
import * as xtpSelectors from './xtp-xdp-connection.selectors';
import { adaptConnectionCfId, getCretaredCfId } from './xtp-xdp-connection.adapters';

export function selectXtpXdpConnection(
  xtpXdpConnection: XtpXdpConnectionProtocol[],
): SelectXtpXdpConnection {
  return {
    type: SELECT_XTP_XDP_CONNECTION,
    payload: xtpXdpConnection,
  };
}

export function updteXtpErrors(errors: { connection: string, protocols: string[]}): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: UPDATE_XTP_XDP_CONNECTION_ERRORS,
      payload: errors
    });
  };
}

export function goToXtpXdpConnectionCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_XTP_XDP_CONNECTION_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${XTP_XDP_CONNECTION}/create`);
  };
}

export function goToXtpXdpConnectionEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_XTP_XDP_CONNECTION_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${XTP_XDP_CONNECTION}/edit`);
  };
}

export function goToXtpXdpConnectionView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_XTP_XDP_CONNECTION_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${XTP_XDP_CONNECTION}/view`);
  };
}

export function goToXtpXdpConnectionList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_XTP_XDP_CONNECTION_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${XTP_XDP_CONNECTION}`);
  };
}

export function getXtpXdpConnectionList(deliveryFunctionId: string): GetXtpXdpConnectionActionThunkAction {
  return async (dispatch: Dispatch) => {
    await dispatch({
      type: GET_XTP_XDP_CONNECTION_LIST,
      payload: api.getXtpXdpsConnectionList(deliveryFunctionId),
    });
  }
}

export function getXtpXdpConnectionById(deliveryFunctionId: string, id: string): GetXtpXdpConnectionByIdActionThunkAction {
  return async (dispatch: Dispatch) => {
    await dispatch({
      type: GET_XTP_XDP_CONNECTION,
      payload: api.getXtpXdpsConnection(deliveryFunctionId, id),
    });
  }
}

export function createXtpXdpConnection(
  deliveryFunctionId: string,
  xtpXdpConnection: WithAuditDetails<CollectionFunctionFormData>,
  xtpXdpConnectionProtocols: WithAuditDetails<XtpXdpConnectionProtocol>[],
  history: History,
): CreateXtpXdpConnectionThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    const errors: { connection: string, protocols: string[] } = { connection: '', protocols: [] };
    let cfResponse: any;

    try {
      cfResponse = await dispatch({
        type: CREATE_XTP_XDP_CONNECTION,
        payload: api.createXtpXdpsConnection(deliveryFunctionId, xtpXdpConnection),
      });
    } catch (e) {
      const errorMessage = i18n.t('xtpXdpConnection.create.failed', { name: xtpXdpConnection.data.name, error: extractErrorMessage(e as AxiosError<ServerResponse<void>>) });
      errors.connection = errorMessage;
      return showError(errorMessage);
    }

    await Promise.all(xtpXdpConnectionProtocols.map(async (protocol) => {
      try {
        await dispatch({
          type: CREATE_XTP_XDP_CONNECTION_PROTOCOL,
          payload: api.createXtpXdpsConnectionProtocol(deliveryFunctionId, adaptConnectionCfId(protocol, getCretaredCfId(cfResponse))),
        });
      } catch (e) {
        const errorMessage = i18n.t('xtpXdpConnection.protocol.create.failed', {
          name: protocol.data.type,
          error: extractErrorMessage(e as AxiosError<ServerResponse<void>>),
        });
        errors.protocols.push(errorMessage);
      }
      return Promise.resolve(protocol);
    }));

    if (errors.connection === '' && errors.protocols.length === 0) {
      showSuccessMessage(i18n.t('xtpXdpConnection.create.success', { name: xtpXdpConnection?.data.name }), 5000);
      dispatch(goToXtpXdpConnectionList(history));
    } else if (errors.connection === '' && errors.protocols.length > 0) {
      dispatch(updteXtpErrors(errors));
      dispatch(goToXtpXdpConnectionList(history));
    } else {
      showError(errors.connection);
    }
  };
}

export function updateXtpXdpConnection(
  deliveryFunctionId: string,
  xtpXdpConnection: WithAuditDetails<XtpXdpConnection>,
  xtpXdpConnectionProtocols: WithAuditDetails<XtpXdpConnectionProtocol>[],
  history: History,
): CreateXtpXdpConnectionThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedXtp] = xtpSelectors.getXtpXdpsConnectionList(getState());
    const errors: { connection: string, protocols: string[] } = { connection: '', protocols: [] };

    await Promise.all(xtpXdpConnectionProtocols.map(async (protocol) => {
      try {
        if (protocol.data.operation === 'DELETE') {
          await dispatch({
            type: UPDATE_XTP_XDP_CONNECTION_PROTOCOL,
            payload: api.deleteXtpXdpsConnectionProtocol(deliveryFunctionId, protocol.data.id, protocol)
          });
        } else {
          await dispatch({
            type: UPDATE_XTP_XDP_CONNECTION_PROTOCOL,
            payload: !protocol.data.id || protocol.data.id === '' ?
              api.createXtpXdpsConnectionProtocol(deliveryFunctionId, adaptConnectionCfId(protocol, selectedXtp.cfid)) :
              api.updateXtpXdpsConnectionProtocol(deliveryFunctionId, protocol.data.id, protocol),
          });
        }

      } catch (e) {
        const errorMessage = i18n.t('xtpXdpConnection.protocol.edit.failed', {
          name: protocol.data.type,
          error: extractErrorMessage(e as AxiosError<ServerResponse<void>>),
        });
        errors.protocols.push(errorMessage);
      }
      return Promise.resolve(protocol);
    }));

    if (errors.connection === '' && errors.protocols.length === 0) {
      showSuccessMessage(i18n.t('xtpXdpConnection.edit.success', { name: xtpXdpConnection?.data.name }), 5000);
      dispatch(goToXtpXdpConnectionList(history));
    } else if (errors.connection === '' && errors.protocols.length > 0) {
      dispatch(updteXtpErrors(errors));
      dispatch(goToXtpXdpConnectionList(history));
    } else {
      showError(errors.connection);
    }
  };
}

export function deleteXtpXtpXdpConnection(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  xtpXdpConnection: XtpXdpConnectionProtocol[],
): DeleteXtpXdpConnectionThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedXtp] = xtpSelectors.getXtpXdpsConnectionList(getState());

    const errors: { connection: string, protocols: string[] } = { connection: '', protocols: [] };

    await Promise.all(xtpXdpConnection.map(async (protocol) => {
      try {
        await dispatch({
          type: UPDATE_XTP_XDP_CONNECTION_PROTOCOL,
          payload: api.deleteXtpXdpsConnectionProtocol(deliveryFunctionId, protocol.id, body)
        });
      } catch (e) {
        const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
        errors.protocols.push(errorMessage);
      }
      return Promise.resolve(protocol);
    }));

    try {
      await dispatch({
        type: DELETE_XTP_XDP_CONNECTION,
        payload: api.deleteXtpXdpsConnection(deliveryFunctionId, window.btoa(selectedXtp.cfid), body),
      });
    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      // errors.connection = i18n.t('xtpXdpConnection.delete.failed', { name: selectedXtp.name, error: errorMessage });
    }

    if (errors.connection === '' && errors.protocols.length === 0) {
      showSuccessMessage(i18n.t('xtpXdpConnection.delete.success', { name: selectedXtp.name }), 5000);
      await dispatch({
        type: GET_XTP_XDP_CONNECTION_LIST,
        payload: api.getXtpXdpsConnectionList(deliveryFunctionId),
      });
    } else if (errors.connection !== '' && errors.protocols.length > 0) {
      showError(errors.connection);
    } else {
      dispatch(updteXtpErrors(errors));
    }
  };
}
